import React, { useRef, useState, useEffect, useCallback } from "react";

export default function AudioTrimmer({
  audioUrl,
  audioDuration,
  maxDuration,
  audioStart,
  clipDuration,
  onStartChange,
  onDurationChange,
}) {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const [waveformData, setWaveformData] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(null); // 'start', 'end', 'region'
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartValues, setDragStartValues] = useState({ start: 0, end: 0 });

  const endTime = Math.min(audioStart + clipDuration, audioDuration);

  // Generate waveform data from audio
  useEffect(() => {
    if (!audioUrl) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    fetch(audioUrl)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => audioContext.decodeAudioData(arrayBuffer))
      .then(audioBuffer => {
        const rawData = audioBuffer.getChannelData(0);
        const samples = 200; // Number of bars in waveform
        const blockSize = Math.floor(rawData.length / samples);
        const filteredData = [];

        for (let i = 0; i < samples; i++) {
          const blockStart = blockSize * i;
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(rawData[blockStart + j]);
          }
          filteredData.push(sum / blockSize);
        }

        // Normalize
        const maxVal = Math.max(...filteredData);
        const normalized = filteredData.map(val => val / maxVal);
        setWaveformData(normalized);
      })
      .catch(err => console.error("Error loading audio:", err));

    return () => audioContext.close();
  }, [audioUrl]);

  // Draw waveform
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || waveformData.length === 0) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const barWidth = width / waveformData.length;
    const startPercent = audioStart / audioDuration;
    const endPercent = endTime / audioDuration;

    // Clear
    ctx.clearRect(0, 0, width, height);

    // Draw background waveform (dimmed)
    ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
    waveformData.forEach((val, i) => {
      const x = i * barWidth;
      const barHeight = val * height * 0.8;
      ctx.fillRect(x, (height - barHeight) / 2, barWidth - 1, barHeight);
    });

    // Draw selected region highlight
    const regionStart = startPercent * width;
    const regionEnd = endPercent * width;
    ctx.fillStyle = "rgba(255, 193, 7, 0.15)";
    ctx.fillRect(regionStart, 0, regionEnd - regionStart, height);

    // Draw selected waveform (highlighted)
    ctx.fillStyle = "#ffc107";
    waveformData.forEach((val, i) => {
      const x = i * barWidth;
      const percent = i / waveformData.length;
      if (percent >= startPercent && percent <= endPercent) {
        const barHeight = val * height * 0.8;
        ctx.fillRect(x, (height - barHeight) / 2, barWidth - 1, barHeight);
      }
    });

    // Draw start handle
    ctx.fillStyle = "#28a745";
    ctx.fillRect(regionStart - 2, 0, 4, height);
    ctx.beginPath();
    ctx.arc(regionStart, height / 2, 8, 0, Math.PI * 2);
    ctx.fill();

    // Draw end handle
    ctx.fillStyle = "#dc3545";
    ctx.fillRect(regionEnd - 2, 0, 4, height);
    ctx.beginPath();
    ctx.arc(regionEnd, height / 2, 8, 0, Math.PI * 2);
    ctx.fill();

    // Draw playhead if playing
    if (isPlaying && currentTime >= audioStart && currentTime <= endTime) {
      const playheadX = (currentTime / audioDuration) * width;
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(playheadX, 0);
      ctx.lineTo(playheadX, height);
      ctx.stroke();
    }
  }, [waveformData, audioStart, endTime, audioDuration, isPlaying, currentTime]);

  // Handle mouse events for dragging
  const getTimeFromX = useCallback((clientX) => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    return percent * audioDuration;
  }, [audioDuration]);

  const handleMouseDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const startX = (audioStart / audioDuration) * rect.width;
    const endX = (endTime / audioDuration) * rect.width;

    // Check if clicking on handles (within 15px)
    if (Math.abs(x - startX) < 15) {
      setIsDragging("start");
    } else if (Math.abs(x - endX) < 15) {
      setIsDragging("end");
    } else if (x > startX && x < endX) {
      setIsDragging("region");
      setDragStartX(e.clientX);
      setDragStartValues({ start: audioStart, end: endTime });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const time = getTimeFromX(e.clientX);

    if (isDragging === "start") {
      const newStart = Math.max(0, Math.min(time, endTime - 1));
      onStartChange(newStart);
      onDurationChange(endTime - newStart);
    } else if (isDragging === "end") {
      const newEnd = Math.min(audioDuration, Math.max(time, audioStart + 1));
      onDurationChange(newEnd - audioStart);
    } else if (isDragging === "region") {
      const deltaX = e.clientX - dragStartX;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const deltaTime = (deltaX / rect.width) * audioDuration;

      const regionLength = dragStartValues.end - dragStartValues.start;
      let newStart = dragStartValues.start + deltaTime;

      // Clamp to bounds
      if (newStart < 0) newStart = 0;
      if (newStart + regionLength > audioDuration) newStart = audioDuration - regionLength;

      onStartChange(newStart);
    }
  }, [isDragging, getTimeFromX, audioStart, endTime, audioDuration, dragStartX, dragStartValues, onStartChange, onDurationChange]);

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // Audio playback controls
  const playSelection = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = audioStart;
    audio.play();
    setIsPlaying(true);
  };

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    setIsPlaying(false);
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    setCurrentTime(audio.currentTime);

    // Stop at end of selection
    if (audio.currentTime >= endTime) {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, "0")}.${ms}`;
  };

  return (
    <div className="audio-trimmer" ref={containerRef}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Waveform Canvas */}
      <div className="waveform-container mb-2" style={{
        background: "rgba(0,0,0,0.3)",
        borderRadius: "8px",
        padding: "8px",
        cursor: isDragging ? "grabbing" : "grab"
      }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={80}
          style={{ width: "100%", height: "80px", display: "block" }}
          onMouseDown={handleMouseDown}
        />
      </div>

      {/* Time Display */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-secondary small">
          <span className="text-success">{formatTime(audioStart)}</span>
          {" → "}
          <span className="text-danger">{formatTime(endTime)}</span>
          {" "}
          <span className="text-warning">({formatTime(clipDuration)} selected)</span>
        </div>
        <div className="text-secondary small">
          Total duration: {formatTime(audioDuration)}
        </div>
      </div>

      {/* Controls */}
      <div className="d-flex gap-2 align-items-center mb-2">
        <button
          type="button"
          className={`btn btn-sm ${isPlaying ? "btn-danger" : "btn-success"}`}
          onClick={isPlaying ? stopPlayback : playSelection}
        >
          {isPlaying ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-1" viewBox="0 0 16 16">
                <path d="M5 3.5h6A1.5 1.5 0 0 1 12.5 5v6a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 11V5A1.5 1.5 0 0 1 5 3.5z"/>
              </svg>
              Stop
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-1" viewBox="0 0 16 16">
                <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
              </svg>
              Play selection
            </>
          )}
        </button>

        <div className="flex-grow-1"></div>

        <div className="d-flex align-items-center gap-2">
          <label className="small text-secondary mb-0">Start:</label>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: "80px" }}
            min="0"
            max={audioDuration - 1}
            step="0.1"
            value={audioStart.toFixed(1)}
            onChange={(e) => onStartChange(parseFloat(e.target.value) || 0)}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="small text-secondary mb-0">Duration:</label>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: "80px" }}
            min="1"
            max={Math.min(audioDuration - audioStart, maxDuration)}
            step="0.5"
            value={clipDuration.toFixed(1)}
            onChange={(e) => onDurationChange(parseFloat(e.target.value) || 1)}
          />
        </div>
      </div>

      {/* Instructions */}
      <div className="text-secondary small">
        <strong>Tip:</strong> Drag the <span className="text-success">green</span> and <span className="text-danger">red</span> handles to select the audio portion, or drag the yellow region to move it.
      </div>
    </div>
  );
}
